import React from "react";
import { minutesToTimeLabel } from "../../classroomSchedule.js";

/* One box, split in two: how much of the day is booked, and how much is
   left. On a lab's day popup and a trainer's alike.

   Both figures arrive decided — GET /classroom-management/schedule/month
   puts `summary` on every day cell (busyMin, freeMin, the counts, and the
   window they were counted within). Nothing is added up here, which is
   what keeps occupied + free equal to the window; two sums in two places
   drift the first time one of them rounds.

   THE WINDOW IS 7:00 AM to 9:00 PM, and the API says so rather than this
   file assuming it. Counting the whole clock made every day read as
   roughly twenty-two hours free — nobody is teaching at three in the
   morning, so that is not capacity going spare.

   The sessions themselves and the bookable slots are NOT repeated here:
   the popup already lists both, above and below. This box answers "how
   much", not "which". */

const hoursText = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (!h) return `${rest}m`;
  return rest ? `${h}h ${rest}m` : `${h}h`;
};

/* One booked stretch or one gap: the exact times in bold, the length
   beside them. The times are the point -- "2h occupied" does not tell a
   coordinator whether the morning or the afternoon is gone. */
const Slot = ({ startMin, endMin, note }) => (
  <div className="cls-hrs-row">
    <span className="cls-hrs-when">
      {minutesToTimeLabel(startMin)}&ndash;{minutesToTimeLabel(endMin)}
    </span>
    {note ? <span className="cls-hrs-what">{note}</span> : <span className="cls-hrs-what" />}
    <span className="cls-hrs-len">{hoursText(endMin - startMin)}</span>
  </div>
);

const TimeSummary = ({ summary, sessions = [], freeWindows = [], dateLabel }) => {
  if (!summary) return null;

  const { windowStartMin, windowEndMin, busyMin, freeMin, busyCount, freeCount } = summary;
  const usableFree = (freeWindows || []).filter((w) => w.endMin > w.startMin);

  return (
    <section className="pgm-section">
      <header className="cls-hrs-head">
        <h5 className="pgm-section-title">Time summary</h5>
        <span className="cls-hrs-note">
          {dateLabel ? `${dateLabel} · ` : ""}counted within{" "}
          {minutesToTimeLabel(windowStartMin)}&ndash;{minutesToTimeLabel(windowEndMin)}
        </span>
      </header>

      {/* Two halves of one box rather than two boxes: they are the same
          day split, and a divider says that better than a gap. */}
      <div className="cls-hrs">
        <div className="cls-hrs-half cls-hrs-half--busy">
          <span className="cls-hrs-label">Occupied</span>
          <span className="cls-hrs-value">{hoursText(busyMin)}</span>
          <span className="cls-hrs-meta">
            {busyCount} class{busyCount === 1 ? "" : "es"}
          </span>
          <div className="cls-hrs-list">
            {(sessions || []).length === 0 ? (
              <p className="cls-hrs-meta">Nothing booked.</p>
            ) : (
              (sessions || []).map((s, i) => (
                <Slot
                  key={`b-${s.startMin}-${i}`}
                  startMin={s.startMin}
                  endMin={s.endMin}
                  note={s.batchName}
                />
              ))
            )}
          </div>
        </div>

        <div className="cls-hrs-half cls-hrs-half--free">
          <span className="cls-hrs-label">Free</span>
          <span className="cls-hrs-value">{hoursText(freeMin)}</span>
          <span className="cls-hrs-meta">
            {freeCount} slot{freeCount === 1 ? "" : "s"}
          </span>
          <div className="cls-hrs-list">
            {usableFree.length === 0 ? (
              <p className="cls-hrs-meta">Booked end to end.</p>
            ) : (
              usableFree.map((w, i) => (
                <Slot key={`f-${w.startMin}-${i}`} startMin={w.startMin} endMin={w.endMin} />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimeSummary;
